import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import {
  adminApi,
  type Application,
  type CredentialGrant,
  type IssuedCredential,
} from '../lib/api.js';
import { useAction, useAsync } from '../lib/useAsync.js';
import { useSession } from '../lib/auth-client.js';
import { breakGlassRole, splitRoles } from '../lib/roles.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { Badge, Button, Field, Note, OutcomeNote, inputStyle } from '../components/ui.js';

/**
 * Issue a service credential.
 *
 * ── This screen used to say issuing was self-service ─────────────────
 *
 * It was, and the reasoning was sound: a key is only usable by whoever receives
 * the secret, so an admin surface that mints one is an admin surface that has
 * seen key material. Better for the holder to call `/api-key/create` themselves.
 *
 * That stopped being possible. `permissions` is a SERVER-ONLY property — the
 * plugin refuses it from any request carrying headers — so a self-service key
 * can never hold one, and a key with no permissions is refused by every route
 * that demands one. There is no path left where the holder mints their own
 * usable credential.
 *
 * So this exists, and the original concern is answered by shape rather than by
 * avoidance: the value is shown once, never stored by the portal, and never
 * re-fetchable. The screen still holds no key material after this modal closes.
 *
 * ── A closed list of grants, not a permissions editor ────────────────
 *
 * The auth service defines what may be granted and validates against it. This
 * offers grants from that list and nothing else — a free-text permissions field
 * here would be a UI for a capability the server would reject anyway, and the
 * rejection would read as a bug in the form. A grant the service adds does not
 * appear here on its own: `CredentialGrant` in lib/api.ts has to be widened too.
 */

/** The auth service's ceiling, restated. Two limits that disagree is a form that
 *  accepts input the service will not — which is exactly how this was found. */
export const MAX_EXPIRY_DAYS = 365;

/**
 * The auth service's `NAME_MAX`, restated.
 *
 * ── THE SAME DEFECT AS THE EXPIRY CEILING, ONE FIELD ACROSS ─────────
 *
 * This box allowed 100 while the service refused anything over 32, so a
 * reasonable name of 33 to 100 characters was accepted by the form and came
 * back a 400 about a limit the screen never mentioned. The service's own
 * comment records that the ceiling used to be 100 there too and was lowered to
 * match the api-key plugin's `maximumNameLength`; this copy was not lowered
 * with it.
 *
 * Two limits that disagree is a form that accepts input the service will not,
 * which is the exact sentence already written twice in this file.
 */
export const MAX_NAME_LENGTH = 32;

/** Shared with modals/EditCredential.tsx, so issuing and editing offer the
 *  same list in the same words. */
export const GRANTS: { value: CredentialGrant; label: string; hint: string }[] = [
  {
    value: 'users:lookup',
    label: 'Resolve an email to a user',
    hint: 'Lets the service test whether an address has an account. Grant it only where that is the point: it is an enumeration capability.',
  },
  {
    value: 'users:read',
    label: 'Read a profile by user id',
    hint: 'Name, username and avatar for an id the service already holds. It learns nothing it did not already know a user for.',
  },
  {
    value: 'connections:declare',
    label: 'Declare this app’s connection controls',
    hint: 'Publishes the settings this app offers on a connection and reads them back. It touches nobody’s data: a person’s own choices are written with their own sign-in.',
  },
  {
    value: 'subscriptions:read',
    label: 'Read what a user pays this app',
    hint: 'The resolved tier for a user id, for this credential’s app only. Separate from reading a profile: a service that only shows names should not learn what people pay.',
  },
  {
    value: 'notifications:send',
    label: 'Send email through Ridgewill',
    hint: 'Queues this app’s emails through the platform Notifications service, which applies the opt-out list and the sending rules. Only email types registered for this app can be sent.',
  },
  {
    value: 'applications:read',
    label: 'List the platform’s applications',
    hint: 'Slug, name and support ticket prefix of every active application. Support needs it to label a ticket with its product. Nothing about any person.',
  },
  {
    value: 'events:publish',
    label: 'Publish platform events',
    hint: 'Lets the service ask Ridgewill to announce an event to the apps that subscribed, such as support telling an app that staff replied. Only the event types that name this app as their publisher are allowed.',
  },
  {
    value: 'groups:read',
    label: 'Check one person’s group membership',
    hint: 'Answers yes or no to “is this user in this security group”, one person and one group at a time. Support uses it to lift the ticket cap for testers. It never lists a group’s members.',
  },
  {
    value: 'staff:read',
    label: 'List support staff to alert',
    hint: 'The verified addresses of everyone holding Global Admin or Support Admin, and no other role. Support emails them when ticket volume spikes. It hands out staff addresses, so grant it only to support.',
  },
];

/**
 * Read the expiry box.
 *
 * Exported and tested because the two failure directions are both silent and
 * only one is visible in the UI. BLANK means "never expires" — a real choice —
 * and anything malformed must NOT fall into that branch: a typo becoming a
 * standing credential is the outcome nobody would notice, since the key works
 * and simply never stops.
 *
 * `Number('')` is 0 and `Number(' ')` is 0, so a naive `Number(x) || undefined`
 * reads blank, whitespace, "0" and "abc" as the same thing. They are not.
 *
 * The ceiling is the auth service's, which is the api-key plugin's
 * `keyExpiration.maxExpiresIn`. This allowed 3650 while the service refused
 * anything over 365, so 366 was accepted by the form and came back a 500.
 */
export function parseExpiry(raw: string): { days?: number; valid: boolean } {
  const text = raw.trim();
  if (text === '') return { valid: true };
  if (!/^\d+$/.test(text)) return { valid: false };
  const days = Number(text);
  return Number.isInteger(days) && days >= 1 && days <= MAX_EXPIRY_DAYS
    ? { days, valid: true }
    : { valid: false };
}

/**
 * The default role every account carries, which is not a role you may put on a
 * credential. The auth service's `ASSIGNABLE_ROLES` excludes it for the same
 * reason: it confers nothing, and offering it invites somebody to "set" it.
 */
const DEFAULT_ROLE = 'user';

/**
 * Which roles this screen may offer, from the signed-in admin's stored role
 * string.
 *
 * Exported and tested because the obvious version is wrong in a way the form
 * hides. `splitRoles` returns everything ON THE ACCOUNT, and every account
 * carries `user` — so offering its result directly puts an option in the list
 * that the auth service refuses with UNKNOWN_ROLE. The rejection then reads as
 * a bug in this screen rather than as the rule it is, which is the exact
 * failure the "only roles you hold" wording is meant to avoid.
 *
 * Nothing else is filtered. A role the portal has not heard of still belongs to
 * this administrator, and dropping it here would quietly withhold an authority
 * they actually have.
 *
 * ── A GLOBAL ADMIN IS OFFERED EVERY ROLE ─────────────────────────────
 *
 * The auth service lets the break-glass role issue any role (RidgeWill/auth#543),
 * because it authorises every action and so anything it grants is a subset of
 * what it holds. Without the role catalogue this cannot know which role that
 * is, so it falls back to the held roles: the narrower list, never a wider one.
 */
export function issuableRoles(
  rawRole: unknown,
  catalogue?: { roles: string[]; groupAssignable?: string[]; breakGlass?: string },
): string[] {
  const held = splitRoles(rawRole).filter((r) => r !== DEFAULT_ROLE);
  const breakGlass = catalogue ? breakGlassRole(catalogue) : undefined;
  if (!catalogue || !breakGlass || !held.includes(breakGlass)) return held;
  return [...new Set([...catalogue.roles.filter((r) => r !== DEFAULT_ROLE), ...held])];
}

/**
 * `issuableRoles` for the signed-in administrator, with the catalogue fetched.
 *
 * While the catalogue loads, or if it fails, this is the held roles alone, so a
 * slow or failed request narrows the picker rather than widening it.
 */
export function useIssuableRoles(rawRole: unknown): string[] {
  const catalogue = useAsync(() => adminApi.roles(), []);
  return issuableRoles(rawRole, catalogue.state.status === 'ready' ? catalogue.state.data : undefined);
}

export function IssueCredential({
  applications,
  onClose,
  onIssued,
  forAgent,
}: {
  applications: Application[];
  onClose: () => void;
  onIssued: () => void;
  /**
   * Issue for this existing agent instead of for the caller (RidgeWill/auth#544).
   * The key is then always an agent's, so that choice is not offered, and the
   * roles are narrowed to ones the agent holds: the service refuses a key that
   * would outrank its agent.
   */
  forAgent?: { id: string; name: string; role?: string | null };
}) {
  const [name, setName] = useState('');
  const [appSlug, setAppSlug] = useState('');
  const [grants, setGrants] = useState<CredentialGrant[]>([]);
  const [expiry, setExpiry] = useState('');
  /**
   * The agent marker and the role it acts with (RidgeWill/platform#31).
   *
   * Two fields rather than one, because they answer different questions: the
   * marker says what WROTE an action, the role says what it may do. A service
   * credential can legitimately carry a role, and an agent can legitimately
   * hold only grants.
   */
  const [agent, setAgent] = useState(false);
  const [role, setRole] = useState('');
  const [issued, setIssued] = useState<IssuedCredential | null>(null);
  /**
   * `null` until asked, then whether the clipboard accepted it.
   *
   * A failure is REPORTED rather than swallowed. `navigator.clipboard` rejects
   * on an insecure origin and when the document is not focused, and a Copy
   * button that silently does nothing is worse than no button on a value you
   * cannot see again — you would close the dialog believing you had it.
   */
  const [copied, setCopied] = useState<boolean | null>(null);
  const action = useAction();
  const { data: session } = useSession();

  const { days, valid: expiryValid } = parseExpiry(expiry);
  const ready = name.trim() !== '' && appSlug !== '' && grants.length > 0 && expiryValid;

  /**
   * Only the roles the signed-in administrator holds, or every role for a
   * Global Admin (`issuableRoles`).
   *
   * The auth service refuses a role the issuer does not hold, which is what
   * stops the credential surface being a role-escalation surface. Offering more
   * than that here would be a form whose options the server rejects, and a
   * rejection that reads as a bug in the screen rather than as the rule it is.
   *
   * `splitRoles` rather than a bare compare: Better Auth stores roles as a
   * COMMA-SEPARATED string, so an account carrying `global-admin,support-admin`
   * is two roles in one column.
   */
  const issuable = useIssuableRoles(session?.user.role);
  const issuerRoles = forAgent
    ? issuable.filter((r) => splitRoles(forAgent.role).includes(r))
    : issuable;

  /**
   * Once a key exists, the form is gone and only the value remains.
   *
   * Not a panel below the form: leaving the fields on screen invites a second
   * submit, and a second submit mints a second credential rather than failing —
   * there is nothing idempotent about issuing.
   */
  if (issued) {
    return (
      <Modal
        title="Credential issued"
        subtitle="Copy it now. This is the only time it is readable."
        onClose={() => {
          onIssued();
          onClose();
        }}
        footer={
          <ModalActions>
            <Button
              onClick={() => {
                onIssued();
                onClose();
              }}
            >
              I have copied it
            </Button>
          </ModalActions>
        }
      >
        <Note tone="danger">
          The auth service stores only a hash of this key. It cannot be shown again, by this screen
          or any other. If it is lost, revoke it and issue another.
        </Note>

        <div style={{ marginTop: '.9rem', display: 'flex', gap: '.5rem', alignItems: 'flex-start' }}>
          {/* `userSelect: all` stays even with a Copy button beside it. The
              clipboard API can refuse — an insecure origin, an unfocused
              document — and a value you cannot see again needs a path that does
              not depend on a browser permission. */}
          <div
            style={{
              flex: 1,
              padding: '.7rem .8rem',
              background: C.surface,
              border: `1px solid ${C.inputBorder}`,
              borderRadius: 6,
              fontFamily: 'ui-monospace, monospace',
              fontSize: '.85rem',
              wordBreak: 'break-all',
              userSelect: 'all',
            }}
          >
            {issued.key}
          </div>
          <Button
            onClick={() => {
              void navigator.clipboard
                .writeText(issued.key)
                .then(() => setCopied(true))
                .catch(() => setCopied(false));
            }}
          >
            {copied === true ? 'Copied' : 'Copy'}
          </Button>
        </div>

        {copied === false && (
          <Note tone="danger">
            The browser refused the clipboard. Select the key above and copy it manually. It is
            still readable until this dialog closes.
          </Note>
        )}

        <div style={{ marginTop: '.9rem', display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          <Badge>{issued.appSlug}</Badge>
          {issued.grants.map((g) => (
            <Badge key={g} tone="good">
              {g}
            </Badge>
          ))}
          <Badge tone={issued.expiresInDays ? 'neutral' : 'warn'}>
            {issued.expiresInDays ? `expires in ${issued.expiresInDays} days` : 'never expires'}
          </Badge>
          {/* Both shown only when set, and both `warn`: they are the two things
              about this key that make it unlike every other service credential,
              and the summary is the last chance to notice a wrong one before
              the value is gone. */}
          {issued.agent && <Badge tone="warn">agent</Badge>}
          {issued.role && <Badge tone="warn">acts as {issued.role}</Badge>}
        </div>

        <Note>
          {forAgent ? (
            <>Issuing is recorded on the agent&rsquo;s activity, with you as the one who issued it.</>
          ) : (
            <>
              Set it as the consuming service's key variable: <code>AUTH_SERVICE_KEY</code> for
              keyodo. Issuing is recorded against your account in the activity log.
            </>
          )}
        </Note>
      </Modal>
    );
  }

  return (
    <Modal
      title={forAgent ? `Issue a key for ${forAgent.name}` : 'Issue a service credential'}
      subtitle={
        forAgent
          ? 'Filed under this agent and marked as its key.'
          : 'A machine-to-machine key, scoped to one application.'
      }
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={!ready || action.running}
            onClick={() =>
              void action.run(async () => {
                const input = {
                  name: name.trim(),
                  appSlug,
                  grants,
                  ...(days === undefined ? {} : { expiresInDays: days }),
                  ...(role ? { role } : {}),
                };
                const result = forAgent
                  ? await adminApi.issueAgentCredential(forAgent.id, input)
                  : await adminApi.issueServiceCredential({ ...input, ...(agent ? { agent: true } : {}) });
                setIssued(result);
                return `Issued ${result.appSlug} credential.`;
              })
            }
          >
            {action.running ? 'Issuing…' : 'Issue credential'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.9rem', marginTop: '.4rem' }}>
        <Field
          label="Name"
          hint={`Who holds it, in words: “keyodo server”. It is what the credential list shows, and an unattributable key is one nobody dares revoke. Up to ${MAX_NAME_LENGTH} characters.`}
        >
          <input
            style={inputStyle}
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(e) => setName(e.target.value)}
            placeholder="keyodo server"
          />
        </Field>

        <Field
          label="Application"
          hint="Whose users this key may see. Not a label: the auth service scopes every lookup to this app, and a user outside it is reported as absent."
        >
          <Select
            block
            label="Application"
            value={appSlug}
            onChange={setAppSlug}
            options={[
              { value: '', label: 'Choose an application…' },
              ...applications.map((a) => ({ value: a.slug, label: `${a.name} (${a.slug})` })),
            ]}
          />
        </Field>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
          <span style={{ fontWeight: 600, color: C.textMid, fontSize: '.85rem' }}>Capabilities</span>
          {GRANTS.map((g) => (
            <label
              key={g.value}
              style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', fontSize: '.85rem' }}
            >
              <input
                type="checkbox"
                checked={grants.includes(g.value)}
                style={{ marginTop: '.2rem' }}
                onChange={(e) =>
                  setGrants((cur) =>
                    e.target.checked ? [...cur, g.value] : cur.filter((v) => v !== g.value),
                  )
                }
              />
              <span>
                <strong>{g.label}</strong>
                <code style={{ color: C.muted, marginLeft: '.4rem', fontSize: '.78rem' }}>
                  {g.value}
                </code>
                <div style={{ color: C.muted, fontSize: '.78rem', lineHeight: 1.4 }}>{g.hint}</div>
              </span>
            </label>
          ))}
        </div>

        {forAgent ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
          <span style={{ fontWeight: 600, color: C.textMid, fontSize: '.85rem' }}>
            Who holds it
          </span>
          <label style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', fontSize: '.85rem' }}>
            <input
              type="checkbox"
              checked={agent}
              style={{ marginTop: '.2rem' }}
              onChange={(e) => setAgent(e.target.checked)}
            />
            <span>
              <strong>An automated agent</strong>
              <div style={{ color: C.muted, fontSize: '.78rem', lineHeight: 1.4 }}>
                Marks everything done with this key as an agent&rsquo;s rather than a
                service&rsquo;s, so the activity log can be filtered to it. It grants nothing and
                withholds nothing on its own.
              </div>
            </span>
          </label>
        </div>
        )}

        <Field
          label="Acts with the role"
          hint={
            forAgent
              ? 'Only roles this agent holds and you may grant are offered. Change the agent\u2019s own role on its Access tab.'
              : 'Service credentials normally carry capabilities and no role at all. A role is the exception, for a credential that stands in for a person. Only roles you hold are offered, because the auth service refuses any other. A Global Admin may give any role.'
          }
        >
          <Select
            block
            label="Role"
            value={role}
            onChange={setRole}
            options={[
              { value: '', label: 'No role (an ordinary service credential)' },
              ...issuerRoles.map((r) => ({ value: r, label: r })),
            ]}
          />
        </Field>

        {role !== '' && (
          <Note tone="danger">
            This credential can do anything the <code>{role}</code> role can, with no sign-in and no
            second factor. Give it an expiry, and store it where that role&rsquo;s own password would
            go.
          </Note>
        )}

        <Field
          label="Expires after (days)"
          hint={`Up to ${MAX_EXPIRY_DAYS} days, the auth service's ceiling. Leave blank for a key that never expires: a real choice, and one nothing will ever prompt anyone to revisit.`}
        >
          <input
            style={inputStyle}
            value={expiry}
            inputMode="numeric"
            placeholder="blank = never"
            onChange={(e) => setExpiry(e.target.value)}
          />
        </Field>

        {!expiryValid && (
          <Note tone="danger">Expiry must be a whole number of days, 1–{MAX_EXPIRY_DAYS}.</Note>
        )}
      </div>
    </Modal>
  );
}
